import { Button } from '@mui/material';
import { Chain } from '@ankr.com/chains-list';
import { Copy } from '@ankr.com/ui';
import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { JWT } from 'domains/jwtToken/types';
import { setSelectedTokenIndex } from 'domains/jwtToken/store/jwtTokenManagerSlice';
import { useAccount } from 'modules/account/hooks/useAccount';
import { useDialog } from 'modules/common/hooks/useDialog';
import { useTranslation } from 'modules/i18n/hooks/useTranslation';

import { CopyEndpointModalContent } from './CopyEndpointModalContent';
import { endpointModalTranslation } from './translation';
import { useCopyEndpointModalStyles } from './useCopyEndpointModalStyles';

interface ICopyEndpointModalProps {
  buttonClassName?: string;
  buttonProps?: React.ComponentProps<typeof Button>;
  chain: Chain;
  hasConsensusSwitch?: boolean;
  hasProjectSelector?: boolean;
  isIconButton?: boolean;
  jwtTokens: JWT[];
  userEndpointToken: string;
}

export const CopyEndpointModalWrapper = ({
  buttonClassName,
  buttonProps,
  chain,
  hasConsensusSwitch,
  hasProjectSelector,
  isIconButton,
  jwtTokens,
  userEndpointToken,
}: ICopyEndpointModalProps) => {
  const { isOpened, onClose: onClickCloseButton, onOpen } = useDialog();

  const dispatch = useDispatch();

  const { accountAddress = '' } = useAccount();

  const handleSelectTokenIndex = useCallback(
    (newIndex: number) => {
      dispatch(
        setSelectedTokenIndex({
          tokenIndex: newIndex,
          address: accountAddress,
        }),
      );
    },
    [accountAddress, dispatch],
  );

  const currentProjectIndex = useMemo(
    () =>
      jwtTokens.find(token => token.userEndpointToken === userEndpointToken)
        ?.index || 0,
    [jwtTokens, userEndpointToken],
  );

  const onClickEndpointButton = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event?.stopPropagation();
      event?.preventDefault();
      handleSelectTokenIndex(currentProjectIndex);
      onOpen();
    },
    [currentProjectIndex, handleSelectTokenIndex, onOpen],
  );

  const { classes, cx } = useCopyEndpointModalStyles();

  const { keys, t } = useTranslation(endpointModalTranslation);

  return (
    <>
      <Button
        size="small"
        className={cx(classes.copyEndpointButton, buttonClassName)}
        {...buttonProps}
        onClick={buttonProps?.onClick || onClickEndpointButton}
      >
        {isIconButton ? <Copy /> : t(keys.copyEndpoint)}
      </Button>
      {isOpened && (
        <CopyEndpointModalContent
          chain={chain}
          currentProjectIndex={currentProjectIndex}
          hasConsensusSwitch={hasConsensusSwitch}
          hasProjectSelector={hasProjectSelector}
          isOpened={isOpened}
          jwtTokens={jwtTokens}
          onClickCloseButton={onClickCloseButton}
          userEndpointToken={userEndpointToken}
        />
      )}
    </>
  );
};
